import { useState } from "react";
import { Button, TextField } from "@legends/ui";
import { resetPassword, type AuthUser } from "./api";

export interface ResetPasswordScreenProps {
  /** The one-time token from the emailed link (?token=…). */
  token: string;
  /** Called on success — the fan is now signed in (the API set a fresh session cookie
   *  and invalidated the old sessions); App adopts the authenticated status. */
  onReset: (user: AuthUser) => void;
  /** Return to the sign-in screen (shown on an invalid/expired link or to request a new one). */
  onBackToLogin: () => void;
}

// Landing page for the reset link (LSC-614) at /reset-password?token=… (resolved before
// AuthGate, like /verify-email). The fan sets a new password; on success they're signed
// in and dropped into the app. An invalid/expired/reused token gives a clear retry path.
export default function ResetPasswordScreen({ token, onReset, onBackToLogin }: ResetPasswordScreenProps) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const missingToken = !token;
  const canSubmit = !busy && !missingToken && password.length >= 8 && password === confirm;

  const submit = () => {
    if (!canSubmit) {
      if (password.length > 0 && password.length < 8) setError("Password must be at least 8 characters.");
      else if (confirm.length > 0 && password !== confirm) setError("Passwords don't match.");
      return;
    }
    setBusy(true);
    setError(null);
    resetPassword(token, password)
      .then((user) => onReset(user))
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : "This reset link is invalid or has expired."),
      )
      .finally(() => setBusy(false));
  };

  return (
    <main
      className="mer-canvas mer-canvas--photo"
      style={{ minHeight: "calc(100vh / var(--ui-zoom, 1))", display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <div
        style={{
          background: "var(--surface-2)",
          border: "1px solid var(--border)",
          padding: "26px 32px 24px",
          width: 360,
          animation: "cnPop var(--dur) var(--ease-out)",
        }}
      >
        <h1 style={{ margin: "0 0 4px", fontSize: "var(--fs-h2)", letterSpacing: "var(--ls-tight)" }}>Set a new password</h1>
        {missingToken ? (
          <div style={{ display: "grid", gap: 14, marginTop: 8 }}>
            <p role="alert" style={{ margin: 0, fontSize: "var(--fs-sm)", color: "var(--alert-2)", lineHeight: 1.6 }}>
              This reset link is missing its token. Request a fresh one from the sign-in screen.
            </p>
            <Button variant="accent" full onClick={onBackToLogin}>
              Back to sign in
            </Button>
          </div>
        ) : (
          <>
            <p style={{ margin: "0 0 4px", fontSize: "var(--fs-sm)", color: "var(--text-dim)" }}>
              Choose a new password. You'll be signed in, and any other sessions will be signed out.
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                submit();
              }}
              style={{ marginTop: 16, display: "grid", gap: 14 }}
            >
              <TextField label="New password" name="password" type="password" value={password} onChange={setPassword} autoFocus full />
              <TextField
                label="Confirm password"
                name="confirmPassword"
                type="password"
                value={confirm}
                onChange={setConfirm}
                onSubmit={submit}
                full
              />
              {error && (
                <p role="alert" style={{ margin: 0, fontSize: "var(--fs-sm)", color: "var(--alert-2)" }}>
                  {error}
                </p>
              )}
              <Button variant="accent" full disabled={!canSubmit} type="submit">
                {busy ? "Saving…" : "Set password & sign in"}
              </Button>
            </form>
            <button
              type="button"
              onClick={onBackToLogin}
              style={{
                marginTop: 16,
                background: "none",
                border: "none",
                color: "var(--accent)",
                cursor: "pointer",
                fontSize: "var(--fs-sm)",
                width: "100%",
              }}
            >
              Back to sign in
            </button>
          </>
        )}
      </div>
    </main>
  );
}
